import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { WasteCycle, WasteProjection } from '@interfaces/ILogistics';
import { formatShortDate, type Lang } from '@utils/dateUtils';
import {
  overTimeSeries,
  perCycleSeries,
  wasteTimeTick,
  type WasteOutlook,
  type WasteOverTimePoint,
} from './wasteChart.logic';
import { wasteAmount } from './wasteFormat';
import styles from './WasteDetail.module.css';

export type WasteChartView = 'perCycle' | 'overTime';

interface IWasteChartProps {
  view: WasteChartView;
  /** Over time only: how far the dashed projection runs. */
  outlook: WasteOutlook;
  history: WasteCycle[];
  projection: WasteProjection | null;
  unit: string;
  tz: string;
}

const CHART_HEIGHT = 180;
const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const AXIS_TICK = { fontSize: 10, fill: 'var(--g500)' } as const;
const GRID_STROKE = 'var(--separator)';
const PROJECTED_DASH = '4 4';

interface ITooltipRow {
  value: number | null;
  dataKey?: string | number;
  payload?: { closedAt?: string; at?: string };
}

interface IWasteTooltipProps {
  active?: boolean;
  payload?: ITooltipRow[];
  unit: string;
  tz: string;
  lang: Lang;
}

/** One line: the date, then the figure — projected figures wear "~". */
export function WasteTooltip({ active, payload, unit, tz, lang }: IWasteTooltipProps) {
  const { t } = useTranslation();
  const row = payload?.find((p) => p.value != null);
  if (!active || !row || row.value == null) return null;
  const at = row.payload?.closedAt || row.payload?.at;
  const projected = row.dataKey === 'projected';
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipWhen}>
        {at ? formatShortDate(at, lang, tz) : t('logistics.waste.next')}
      </span>
      <span>
        {projected ? '~' : ''}
        {wasteAmount(t, row.value, unit)}
      </span>
    </div>
  );
}

interface IProjectedDotProps {
  cx?: number;
  cy?: number;
  payload: WasteOverTimePoint;
}

/** The projected line's mark. The dash starts AT the last recorded close, so
 *  it continues the solid line — but that point is a close, and keeps the
 *  recorded dot: only the projection's own points wear the hollow one. */
export function ProjectedDot({ cx, cy, payload }: IProjectedDotProps) {
  if (payload.recorded != null) return <g />;
  return (
    <circle cx={cx} cy={cy} r={4} fill="var(--white)" stroke="var(--status-planned-text)" strokeWidth={2} />
  );
}

/**
 * Per cycle: a bar per closed cycle plus one dashed bar at the projected
 * average. Over time: the cumulative line, continued dashed to the 6- and
 * 12-month marks, or only to the next expected close (`outlook`). One hue for what was recorded, the planned blue for what
 * is projected — the legend names both, so colour is never the only cue.
 */
export function WasteChart({ view, outlook, history, projection, unit, tz }: IWasteChartProps) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tooltip = <WasteTooltip unit={unit} tz={tz} lang={lang} />;
  // Recharts compares chart-element props by reference; a fresh array every
  // render would defeat its internal memoisation (same as the other charts).
  const perCycle = useMemo(
    () => (view === 'perCycle' ? perCycleSeries(history, projection) : []),
    [view, history, projection],
  );
  const overTime = useMemo(
    () => (view === 'overTime' ? overTimeSeries(history, projection, outlook) : []),
    [view, history, projection, outlook],
  );

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        {view === 'perCycle' ? (
          <BarChart data={perCycle} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} />
            <XAxis
              dataKey="cycle"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: GRID_STROKE }}
              tickFormatter={(cycle: number) =>
                cycle > history.length ? t('logistics.waste.next') : formatShortDate(history[cycle - 1]!.closedAt, lang, tz)
              }
            />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={36} />
            <Tooltip content={tooltip} cursor={{ fill: 'var(--status-planned-bg)' }} />
            {/* One stack, so the recorded and projected bars share a slot and sit
                centred over their label — each cycle carries exactly one of the
                two, so nothing ever actually stacks. Side by side, every slot
                held an empty twin and the visible bar sat off its date. */}
            <Bar dataKey="waste" stackId="cycle" fill="var(--bar-blue)" radius={BAR_RADIUS} isAnimationActive={false} maxBarSize={36} />
            <Bar
              dataKey="projected"
              stackId="cycle"
              fill="var(--white)"
              stroke="var(--status-planned-text)"
              strokeDasharray={PROJECTED_DASH}
              radius={BAR_RADIUS}
              isAnimationActive={false}
              maxBarSize={36}
            />
          </BarChart>
        ) : (
          <LineChart data={overTime} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} />
            {/* A real time scale: the projection's 6- and 12-month marks sit at
                their true distance, not one cycle-width after the last close. */}
            <XAxis
              dataKey="ms"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: GRID_STROKE }}
              tickFormatter={(ms: number) => wasteTimeTick(ms, lang, tz)}
            />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={36} />
            <Tooltip content={tooltip} />
            <Line
              dataKey="recorded"
              stroke="var(--g700)"
              strokeWidth={2}
              dot={{ r: 4, fill: 'var(--g700)', strokeWidth: 0 }}
              isAnimationActive={false}
            />
            <Line
              dataKey="projected"
              stroke="var(--status-planned-text)"
              strokeWidth={2}
              strokeDasharray={PROJECTED_DASH}
              dot={ProjectedDot}
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
      <div className={styles.legend}>
        <span>
          {/* The swatch matches the mark it names: a bar in one view, a line in the other. */}
          <i className={view === 'perCycle' ? styles.swatchRecorded : styles.swatchRecordedLine} aria-hidden="true" />
          {t('logistics.waste.legendRecorded')}
        </span>
        {projection ? (
          <span>
            <i className={styles.swatchProjected} aria-hidden="true" />
            {t(view === 'overTime' && outlook === 'nextCycle' ? 'logistics.waste.legendNextCycle' : 'logistics.waste.legendProjected')}
          </span>
        ) : null}
      </div>
    </div>
  );
}
